import type { ComponentType } from "react";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import type { SvgIconProps } from "@mui/material";

type NotifySeverity = "info" | "warning" | "error" | "success";

export interface EventPresentation {
  Icon: ComponentType<SvgIconProps>;
  /** MUI palette key for the icon. */
  color: SvgIconProps["color"];
  /** Severity for the arrival toast, so toast and row agree. */
  severity: NotifySeverity;
  /**
   * Label for the link out. Must name where the click actually lands — the host
   * routes a guideline breach to the portfolio's Guidelines tab, so "View
   * portfolio" would be a mislabel.
   */
  linkLabel: string;
  /**
   * Whether the subject is somewhere a user can be sent. An async task is a row in
   * AsyncTaskStatus, not a page — routing its id as if it were an investment is
   * how "View guidelines" ended up pointing at /investments/<taskId>/guidelines.
   */
  navigable: boolean;
}

/**
 * How each event type looks. Deliberately frontend-only and keyed off
 * `event_type`, which every row already carries: an icon stored on the row would
 * mean the worker choosing UI, and would freeze at whatever was styled the day it
 * was written.
 */
const PRESENTATION: Record<string, EventPresentation> = {
  GUIDELINE_BREACH: {
    Icon: WarningAmberIcon,
    color: "warning",
    severity: "warning",
    linkLabel: "View guidelines",
    navigable: true,
  },
  NOTE_MENTION: {
    Icon: AlternateEmailIcon,
    color: "info",
    severity: "info",
    linkLabel: "View note",
    navigable: true,
  },
  NOTE_ADDED: {
    Icon: StickyNote2Icon,
    color: "action",
    severity: "info",
    linkLabel: "View note",
    navigable: true,
  },
  AGENT_MESSAGE: {
    Icon: AutoAwesomeIcon,
    color: "info",
    severity: "info",
    linkLabel: "Open conversation",
    navigable: true,
  },
  SKILL_RUN_COMPLETED: {
    Icon: AutoAwesomeIcon,
    color: "info",
    severity: "info",
    linkLabel: "Open conversation",
    navigable: true,
  },
  SKILL_RUN_FAILED: {
    Icon: AutoAwesomeIcon,
    color: "error",
    severity: "error",
    // A run that died before creating a conversation has subjectId 0 and is
    // filtered out by isNavigable below, so this label only shows when there
    // is something to open.
    linkLabel: "Open conversation",
    navigable: true,
  },
  TASK_COMPLETED: {
    Icon: TaskAltIcon,
    color: "success",
    severity: "success",
    linkLabel: "",
    navigable: false,
  },
  TASK_FAILED: {
    Icon: ErrorOutlineIcon,
    color: "error",
    severity: "error",
    linkLabel: "",
    navigable: false,
  },
};

const FALLBACK: EventPresentation = {
  Icon: NotificationsNoneIcon,
  color: "action",
  severity: "info",
  linkLabel: "View details",
  // Unknown types get no link: we cannot know where their subject lives.
  navigable: false,
};

/**
 * Unknown types fall back rather than throw: a server that has shipped a new
 * event type must not break a browser tab still running the previous frontend.
 */
export function getEventPresentation(eventType: string): EventPresentation {
  return PRESENTATION[eventType] ?? FALLBACK;
}

/**
 * Whether THIS row can be opened — the event type has somewhere to go AND the
 * row names a subject.
 *
 * Both are needed: conversation-subject events carry subjectId 0 when there was
 * no conversation (an agent message from an external MCP client, a skill run
 * that failed before starting one), and a link that loads conversation 0 is a
 * dead end. Single helper so the row and its toast can never disagree.
 */
export function isNavigable(notification: {
  eventType: string;
  subjectId: number;
}): boolean {
  return (
    getEventPresentation(notification.eventType).navigable &&
    notification.subjectId > 0
  );
}
